import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CommunityViewPublic,
  PublicUserProfileView,
  CampaignPublicView,
} from "@bbb/model";
import { CommunityAdminSection, ViewTypes } from "@types";
import { campaignProgressStorage } from "./localStorage";
import {
  usePrivateCampaigns,
  useLiveCampaigns,
} from "../stores/useCampaignStore";
import { getLatestCampaignStatus } from "@bbb/utils";
import { protectedViews } from "@constants";

const EMPTY_CAMPAIGNS: any[] = [];

function buildLeaderboardPath(options: {
  communityId?: string;
  campaignId?: string;
}) {
  const params = new URLSearchParams();

  if (options.campaignId) {
    params.set("scope", "campaign");
    params.set("campaignId", options.campaignId);
  } else if (options.communityId) {
    params.set("scope", "community");
    params.set("communityId", options.communityId);
  }

  const query = params.toString();
  return query ? `/leaderboards?${query}` : "/leaderboards";
}

export const buildPathForView = (
  view: ViewTypes,
  options: { communityId?: string; campaignId?: string; userId?: string } = {},
) => {
  const { communityId, campaignId, userId } = options;
  switch (view) {
    case "leaderboard":
      return buildLeaderboardPath({ communityId, campaignId });
    case "achievements":
      return "/user-achievements";
    case "active-quests":
      return "/user-active-quests";
    case "profile-settings":
      return "/user-profile-edit";
    case "public-profile":
      return userId ? `/user-profile/${userId}` : "/user-profile";
    case "referrals":
      return "/user-referrals";
    case "rewards-hub":
      return "/user-rewards-hub";
    case "badges-info":
      return "/badge-info";
    case "ambassador-hub":
      return "/ambassador-hub";
    case "communities":
      return "/communities";
    case "create-community":
      return "/communities/create";
    case "community-admin":
      return communityId
        ? `/communities/${communityId}/manage`
        : "/communities/manage";
    case "privacy-policy":
      return "/privacy-policy";
    case "terms-of-service":
      return "/terms-of-service";
    case "dashboard":
      return communityId ? `/communities/${communityId}` : "/";
    case "community-leaderboard":
      return buildLeaderboardPath({ communityId });
    case "detail":
      return communityId && campaignId
        ? `/communities/${communityId}/campaigns/${campaignId}`
        : communityId
          ? `/communities/${communityId}`
          : "/";
    case "list":
    default:
      return "/";
  }
};

const parsePathname = (
  pathname: string,
): { view: ViewTypes; communityId?: string; campaignId?: string; userId?: string } => {
  const segments = pathname.split("/").filter(Boolean);
  let view: ViewTypes = "list";
  let communityId: string | undefined;
  let campaignId: string | undefined;
  let userId: string | undefined;

  if (segments.length === 0) {
    view = "list";
  } else if (segments[0] === "leaderboards" || segments[0] === "global-leaderboard") {
    view = "leaderboard";
  } else if (segments[0] === "user-achievements") {
    view = "achievements";
  } else if (segments[0] === "user-active-quests") {
    view = "active-quests";
  } else if (segments[0] === "user-profile-edit") {
    view = "profile-settings";
  } else if (segments[0] === "user-profile") {
    view = "public-profile";
    userId = segments[1];
  } else if (segments[0] === "user-referrals") {
    view = "referrals";
  } else if (segments[0] === "user-rewards-hub") {
    view = "rewards-hub";
  } else if (segments[0] === "badge-info") {
    view = "badges-info";
  } else if (segments[0] === "ambassador-hub") {
    view = "ambassador-hub";
  } else if (segments[0] === "privacy-policy") {
    view = "privacy-policy";
  } else if (segments[0] === "terms-of-service") {
    view = "terms-of-service";
  } else if (segments[0] === "communities") {
    const maybeId = segments[1];
    if (!maybeId || maybeId === "verified") {
      view = "communities";
    } else if (maybeId === "create") {
      view = "create-community";
    } else if (maybeId === "manage") {
      view = "community-admin";
    } else if (maybeId) {
      communityId = maybeId;
      if (segments[2] === "manage") {
        view = "community-admin";
      } else if (segments[2] === "leaderboard") {
        view = "leaderboard";
      } else if (segments[2] === "campaigns" && segments[3]) {
        campaignId = segments[3];
        view = "detail";
      } else {
        view = segments[2] === "guest-view" ? "detail" : "dashboard";
      }
    }
  }

  return { view, communityId, campaignId, userId };
};

export function shouldRedirectMissingCommunityAdminRoute({
  view,
  routeCommunityId,
  communities,
  communitiesLoaded,
  adminCommunityIds = [],
  adminCommunitiesLoaded = true,
}: {
  view: ViewTypes;
  routeCommunityId?: string;
  communities: CommunityViewPublic[];
  communitiesLoaded: boolean;
  adminCommunityIds?: string[];
  adminCommunitiesLoaded?: boolean;
}) {
  // On refresh, the owner/admin query can resolve before the shared community
  // store has been synchronized. An empty list in that window is ambiguous, so
  // do not treat it as proof the requested admin route is invalid.
  if (view !== "community-admin") {
    return false;
  }

  if (routeCommunityId && adminCommunityIds.includes(routeCommunityId)) {
    return false;
  }

  if (!adminCommunitiesLoaded) {
    return false;
  }

  if (routeCommunityId && adminCommunityIds.length === 0) {
    return false;
  }

  return (
    communitiesLoaded &&
    communities.length > 0
  );
}

type RoutingParams = {
  viewMode: ViewTypes;
  setViewMode: (view: ViewTypes) => void;
  selectedCommunity: CommunityViewPublic | null;
  selectedCampaign: CampaignPublicView | null;
  setSelectedCommunity: (community: CommunityViewPublic | null) => void;
  setSelectedCampaign: (CampaignPublicView: any | null) => void;
  communities: CommunityViewPublic[];
  isAuthenticated: boolean;
  displayUser: PublicUserProfileView | null | undefined;
  setShowLoginDialog: (open: boolean) => void;
  setPublicProfileUserId: (userId: string | null) => void;
  isAuthReady?: boolean;
  communitiesLoaded?: boolean;
  isUserProfileLoading?: boolean;
  preferPrivateCampaigns?: boolean;
  adminCommunityIds?: string[];
  adminCommunitiesLoaded?: boolean;
};

export function useDashboardRouting({
  viewMode,
  setViewMode,
  selectedCommunity,
  selectedCampaign,
  setSelectedCommunity,
  setSelectedCampaign,
  communities,
  isAuthenticated,
  displayUser,
  setShowLoginDialog,
  setPublicProfileUserId,
  isAuthReady = true,
  communitiesLoaded = false,
  isUserProfileLoading = false,
  preferPrivateCampaigns = false,
  adminCommunityIds = [],
  adminCommunitiesLoaded = true,
}: RoutingParams) {
  const navigate = useNavigate();
  const location = useLocation();
  const communityId = selectedCommunity?.id;
  const lastSyncedPathRef = useRef<string | null>(null);

  const { data: ownerCampaignsData, isLoading: isOwnerCampaignsLoading } =
    usePrivateCampaigns(communityId, preferPrivateCampaigns);
  const { data: publicCampaignsData, isLoading: isPublicCampaignsLoading } =
    useLiveCampaigns(communityId, !preferPrivateCampaigns);
  const ownerCampaigns = ownerCampaignsData ?? EMPTY_CAMPAIGNS;
  const publicCampaigns = publicCampaignsData ?? EMPTY_CAMPAIGNS;

  const navigateToView = useCallback(
    (
      view: ViewTypes,
      options: {
        communityId?: string;
        campaignId?: string;
        userId?: string;
        replace?: boolean;
      } = {},
    ) => {
      setViewMode(view);
      const shouldCarryCommunity =
        view === "dashboard" ||
        view === "community-admin" ||
        view === "detail" ||
        view === "community-leaderboard";
      const shouldCarryCampaign = view === "detail";
      const path = buildPathForView(view, {
        communityId:
          options.communityId ?? (shouldCarryCommunity ? selectedCommunity?.id : undefined),
        campaignId:
          options.campaignId ?? (shouldCarryCampaign ? selectedCampaign?.id : undefined),
        userId: options.userId,
      });
      const target = path || "/";
      const currentPath = `${location.pathname}${location.search}`;
      if (target !== currentPath) {
        navigate(target, { replace: options.replace });
      }
    },
    [
      location.pathname,
      location.search,
      navigate,
      selectedCommunity?.id,
      selectedCampaign?.id,
      setViewMode,
    ],
  );

  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    if (location.pathname === "/global-leaderboard") {
      navigate(buildLeaderboardPath({}), { replace: true });
      return;
    }

    if (segments[0] === "communities" && segments[1] && segments[2] === "leaderboard") {
      navigate(buildLeaderboardPath({ communityId: segments[1] }), { replace: true });
      return;
    }

    const pathKey = `${location.pathname}${location.search}`;
    const { view, communityId, campaignId, userId } = parsePathname(location.pathname);

    // Only sync viewMode when the URL actually changed. This prevents the
    // effect from re-triggering itself when other deps (queries, auth, etc.)
    // update without a navigation.
    const pathChanged = lastSyncedPathRef.current !== pathKey;
    if (pathChanged) {
      lastSyncedPathRef.current = pathKey;
      if (view !== viewMode) {
        setViewMode(view);
      }
    }

    if (view === "public-profile") {
      setPublicProfileUserId(userId ?? null);
    } else {
      setPublicProfileUserId(null);
    }

    if (communityId) {
      const found = communities.find(
        (community) => community.id === communityId,
      );
      if (found) {
        if (!selectedCommunity || selectedCommunity.id !== found.id) {
          setSelectedCommunity(found);
        }
        const campaigns =
          (preferPrivateCampaigns
            ? ownerCampaigns
            : isAuthenticated && ownerCampaigns.length > 0
            ? ownerCampaigns
            : publicCampaigns) ?? [];

        if (!isOwnerCampaignsLoading && !isPublicCampaignsLoading) {
          if (campaignId) {
            const foundCampaign = campaigns.find((c) => c.id === campaignId);
            if (foundCampaign) {
              setSelectedCampaign(foundCampaign);
            }
          } else {
            const hasSelected = selectedCampaign
              ? campaigns.some((c) => c.id === selectedCampaign.id)
              : false;

            // Only auto-pick a campaign when none is selected or the current
            // selection no longer exists in the list. This prevents overriding
            // a user-selected campaign (e.g., second/third card click).
            if (!hasSelected) {
              const liveCampaigns = campaigns.filter(
                (c) => getLatestCampaignStatus(c) === "live",
              );
              const nextCampaign =
                liveCampaigns.length > 0 ? liveCampaigns[0] : campaigns[0];
              if (nextCampaign) {
                setSelectedCampaign(nextCampaign);
              }
            }
          }
        }
        if (view === "dashboard" && isAuthenticated && displayUser) {
          campaignProgressStorage.startCampaign(displayUser.id, found.id);
        }
      } else if (
        shouldRedirectMissingCommunityAdminRoute({
          view,
          routeCommunityId: communityId,
          communities,
          communitiesLoaded,
          adminCommunityIds,
          adminCommunitiesLoaded,
        })
      ) {
        // Invalid / missing community id for admin routes – send the user home.
        navigate("/", { replace: true });
        setViewMode("list");
        setSelectedCommunity(null);
        setSelectedCampaign(null);
        setPublicProfileUserId(null);
        return;
      }
    } else if (view === "list") {
      if (selectedCommunity) setSelectedCommunity(null);
      if (selectedCampaign) setSelectedCampaign(null);
    }

    if (!isAuthReady) {
      return;
    }

    if (
      protectedViews.includes(view) &&
      (!isAuthenticated || (!displayUser && !isUserProfileLoading))
    ) {
      setShowLoginDialog(true);
      if (location.pathname !== "/") {
        navigate("/", { replace: true });
      }
      setViewMode("list");
      setSelectedCommunity(null);
      setSelectedCampaign(null);
      setPublicProfileUserId(null);
      return;
    }
  }, [
    communities,
    displayUser,
    isAuthenticated,
    location.pathname,
    location.search,
    navigate,
    selectedCampaign,
    selectedCommunity,
    setSelectedCampaign,
    setSelectedCommunity,
    setShowLoginDialog,
    setPublicProfileUserId,
    setViewMode,
    viewMode,
    isAuthReady,
    communitiesLoaded,
    adminCommunityIds,
    adminCommunitiesLoaded,
    ownerCampaigns,
    publicCampaigns,
    preferPrivateCampaigns,
    isOwnerCampaignsLoading,
    isPublicCampaignsLoading,
    isUserProfileLoading,
  ]);

  return { navigateToView };
}

export const buildCommunityAdminSectionPath = (
  communityId: string,
  section: CommunityAdminSection,
) => {
  const segment = section || "quests";
  return `/communities/${communityId}/manage/${segment}`;
};

export const parseCommunityAdminSection = (
  pathname: string,
): { communityId?: string; section?: CommunityAdminSection } => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "communities" || segments[2] !== "manage") return {};
  const communityId = segments[1];
  const remaining = segments.slice(3).join("/");
  return { communityId, section: remaining as CommunityAdminSection };
};
